import React, { useEffect } from 'react';
import { useLang } from '~/hooks/useLang';
import useOccurrenceState from '~/features/Occurrence/stores/OccurrenceState'
import useOccurrenceComentState from '~/features/Occurrence/stores/OccurrenceCommentState';
import { SendCommentField } from '~/components/SendCommentField';
import { DisplayComments } from '~/components/DisplayComments';
import { Grid } from '@mui/material';
import { useHookstate, Downgraded } from '@hookstate/core';
import { CommentDataProps } from '~/features/Dispatch/stores/DispatchCommentState';
import OccurrenceListDispatches from '~/features/Occurrence/components/OccurrenceListDispatches';
import EventInputContainerOccurrence from '~/features/Occurrence/components/EventInputContainerOccurrence';
import { Validation as validation } from '@hookstate/validation';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from '~/components/ErrorFallback';
import ButtonActionsOccurrenceContainer from '~/features/Occurrence/components/ButtonActionsOccurrenceContainer';
import EventInputRequesterOccurrence from '~/features/Occurrence/components/EventInputRequesterOccurrence';
import useSystemState from '~/features/System/stores/SystemState';
import { LatLng } from 'leaflet';
import { useMarkerState } from '~/features/Map/stores/MapState';
import OccurrenceDisplayComments from '../OccurrenceDisplayComments';
import type IDispatchOccurrence from '../../interfaces/IDispatchOccurrence';
import { getDispatchesForOccurrenceById } from '../../services';

export default function OccurrenceForm() {
  const { translate } = useLang();
  const { updateMarker } = useMarkerState()
  const { newComment, sendComment } = useOccurrenceComentState();
  const { occurrence } = useOccurrenceState();
  const { systemConfig } = useSystemState();
  const { createUniqueMarker, forceViewMap } = useMarkerState();
  const moveScrolltoComment = useHookstate(false);
  const loadingSendComment = useHookstate(false)
  const isValid = validation(occurrence().id).valid()
  const dispatches = useHookstate<IDispatchOccurrence[]>([])
  const loading = useHookstate(false)
  const retryConnect = useHookstate(false)
  const forceRefresh = useHookstate(false)


  const getOccurrencesById = async () => {
    try {
      loading.set(true);
      dispatches.set([]);
      const response = await getDispatchesForOccurrenceById({ id: occurrence().id.get() })
      dispatches.set(response)
    } catch (error) {
      retryConnect.set(true);
      console.error('Erro ao buscar dispatches:', error);
      throw error;
    } finally {
      loading.set(false);
    }
  }

  useEffect(() => {
    getOccurrencesById();
  }, [occurrence().id.get(), occurrence().closed_at.get(), forceRefresh.get()])

  useEffect(() => {
    // syncPreviousComments(occurrence().id.get())
    if (isValid) {
      const params = {
        id: occurrence().id.get(),
        isCurrent: true,
        position: new LatLng(occurrence().latitude.get() ?? 0, occurrence().longitude.get() ?? 0),
      }
      createUniqueMarker({
        uniqueReference: true,
        markerType: 'occurrence',
        markerIdentifier: params.id,
        isCurrent: true,
        typeName: occurrence().type.name.get(),
        subtypeName: occurrence().subtype.name.get(),
        location: occurrence().location.get(),
        ...params as any
      });
      forceViewMap().position.set(params.position);
    }

    return () => {
      updateMarker({ markerType: 'occurrence', markerIdentifier: occurrence().id.get(), attribute: 'isCurrent', value: false })
    }
  }, [occurrence().id.get()])


  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Grid container columns={12} sx={{ margin: 0, alignItems: 'center', justifyContent: 'flex-start' }}>
        <Grid container sx={{ margin: 0 }}>
          <Grid item md={4}>
            <EventInputContainerOccurrence
              isValid={isValid}
              isValidEdit={false}
              canEdit={occurrence().closed_at.get() === null && (systemConfig().allowedEditTypeSubtypeOfOccurrence.get() === '1' || systemConfig().allowedEditAddressOfOccurrence.get() === '1')}
              isInvalidRequest={false}
              id={occurrence().id}
              lat={occurrence().latitude}
              lon={occurrence().longitude}
              typeId={occurrence().type.id}
              typeName={occurrence().type.name}
              typeCode={occurrence().type.code}
              subtypeId={occurrence().subtype.id}
              subTypeName={occurrence().subtype.name}
              subtypeCode={occurrence().subtype.code}
              cityId={occurrence()?.city?.id}
              districtId={occurrence()?.district?.id}
              type={'occurrence'}
              location={occurrence()?.location}
              reference={occurrence()?.address_reference}
              totalSubtypes={occurrence()?.totalSubtypes}
              cityName={occurrence()?.city.name}
              districtName={occurrence()?.district.name}
              totalDistricts={occurrence()?.totalDistricts}
            />
            <EventInputRequesterOccurrence canEdit={occurrence().closed_at.get() === null && systemConfig().allowedEditRequesterDataOfOccurrence.get() === '1'} />

          </Grid>

          <Grid item md={4} sx={{ paddingLeft: 1 }}>
            <Grid sx={{ width: '100%', margin: 0 }} direction={'column'}>
              <Grid item mb={1} md={1} xs={1}>
                <SendCommentField
                  disabled={Boolean(occurrence()?.closed_at.get()) && !(systemConfig().allowedCommentOnClosedOccurrence.get() === '1')}
                  id={occurrence().id.get()}
                  name="comment"
                  loading={loadingSendComment}
                  label={translate('Comment')}
                  required={true}
                  handleClickSend={() => console.log('Enviando')}
                  newComment={newComment}
                  sendComment={sendComment}
                  uploadImageOption={false}
                  moveScrolltoComment={moveScrolltoComment}
                />
              </Grid>
              <Grid item md={12} height={360} sx={{ overflowY: 'auto', width: '100%' }}>
                <OccurrenceDisplayComments moveScrolltoComment={moveScrolltoComment.get()} />
              </Grid>
            </Grid>
          </Grid>

          <Grid item md={4} sx={{ paddingLeft: 1 }}>
            <OccurrenceListDispatches
              dispatches={dispatches}
              loading={loading.get()}
              retryConnect={retryConnect.get()}
              forceRefresh={forceRefresh}
            />
          </Grid>

          <Grid item columnSpacing={1} md={12} sx={{ mt: '10px' }}>
            <ButtonActionsOccurrenceContainer
              dispatches={dispatches}
            />
          </Grid>
        </Grid>

      </Grid>
    </ErrorBoundary>
  )
}